import { useState } from "react";
import type { KeyboardEvent } from "react";

import type { MoodQuadrant } from "./MoodQuadrantGrid";

// WordChips — single-select feeling-word chips under the mood quadrant
// (Homebase pre-session flow). Optional "+ add your own" free-text escape
// hatch: the custom word commits through the same onChange as a preset chip
// (the server never validates against the preset lists). A committed value
// outside `words` renders as a selected chip so flow re-entry shows the
// stored custom word.

export interface WordChipsProps {
    words: string[];
    value: string | null;
    onChange: (word: string) => void;
    allowCustom?: boolean;
    /** Paint the selected chip (and hover, and the custom-word field) in a mood
     *  quadrant's --mood-* colours, so the word visibly belongs to the circle picked
     *  above it. Omitted ⇒ the standard --interactive selection, unchanged. */
    tone?: MoodQuadrant;
    /** Row alignment of the wrapped chips. Default `start`; `center` for a chip
     *  cloud sitting under centred content (the check-in's mood circles). */
    align?: "start" | "center";
}

// Literal class strings per tone so Tailwind's scanner sees every one.
const TONE = {
    selected: {
        red: "border-mood-red-bd bg-mood-red-bg text-primary",
        yellow: "border-mood-yellow-bd bg-mood-yellow-bg text-primary",
        blue: "border-mood-blue-bd bg-mood-blue-bg text-primary",
        green: "border-mood-green-bd bg-mood-green-bg text-primary",
    },
    idle: {
        red: "border-border bg-surface text-secondary hover:border-mood-red-bd hover:text-primary",
        yellow: "border-border bg-surface text-secondary hover:border-mood-yellow-bd hover:text-primary",
        blue: "border-border bg-surface text-secondary hover:border-mood-blue-bd hover:text-primary",
        green: "border-border bg-surface text-secondary hover:border-mood-green-bd hover:text-primary",
    },
    addHover: {
        red: "hover:border-mood-red-bd",
        yellow: "hover:border-mood-yellow-bd",
        blue: "hover:border-mood-blue-bd",
        green: "hover:border-mood-green-bd",
    },
    field: {
        red: "border-mood-red-bd",
        yellow: "border-mood-yellow-bd",
        blue: "border-mood-blue-bd",
        green: "border-mood-green-bd",
    },
} as const;

export function WordChips({
    words,
    value,
    onChange,
    allowCustom = false,
    tone,
    align = "start",
}: WordChipsProps) {
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState("");

    const chips = value !== null && !words.includes(value) ? [...words, value] : words;

    function commitOnKey(event: KeyboardEvent<HTMLButtonElement>, word: string) {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onChange(word);
        }
    }

    function commitDraft() {
        const word = draft.trim();
        if (!word) return;
        onChange(word);
        setAdding(false);
        setDraft("");
    }

    return (
        <div className={`flex flex-wrap items-center gap-2 ${align === "center" ? "justify-center" : ""}`}>
            {chips.map((word) => {
                const selected = value === word;
                return (
                    <button
                        key={word}
                        type="button"
                        aria-pressed={selected}
                        onClick={() => onChange(word)}
                        onKeyDown={(e) => commitOnKey(e, word)}
                        // Same chip as ToggleChips (selection audit, 2026-09-15) — 1px
                        // --interactive border over the tint when selected.
                        // it used to select to a solid --cta-ink fill with white
                        // text — a second chip language for the same idea. No ×
                        // on the selected chip, unlike ToggleChips: this is
                        // single-select, and tapping the chosen word does not
                        // clear it, so an × would promise an action that isn't there.
                        className={[
                            "inline-flex items-center whitespace-nowrap rounded-[var(--radius-pill)] border px-4 py-2",
                            "font-sans text-base font-medium cursor-pointer",
                            "transition-[border-color,background-color,color] duration-[var(--dur-base)] ease-[var(--ease-out)]",
                            selected
                                ? tone
                                    ? TONE.selected[tone]
                                    : "border-interactive bg-interactive-soft text-primary"
                                : tone
                                  ? TONE.idle[tone]
                                  : "border-border bg-surface text-secondary hover:border-interactive hover:text-primary",
                        ].join(" ")}
                    >
                        {word}
                    </button>
                );
            })}
            {allowCustom && !adding ? (
                <button
                    type="button"
                    onClick={() => setAdding(true)}
                    className={`inline-flex items-center whitespace-nowrap rounded-[var(--radius-pill)] border border-dashed border-border bg-surface px-4 py-2 font-sans text-base font-medium text-tertiary cursor-pointer transition-colors hover:text-primary ${
                        tone ? TONE.addHover[tone] : "hover:border-interactive"
                    }`}
                >
                    + add your own
                </button>
            ) : null}
            {allowCustom && adding ? (
                <input
                    autoFocus
                    type="text"
                    aria-label="Add your own word"
                    value={draft}
                    maxLength={40}
                    placeholder="Your word…"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            commitDraft();
                        }
                        if (e.key === "Escape") {
                            setAdding(false);
                            setDraft("");
                        }
                    }}
                    onBlur={commitDraft}
                    className={`rounded-[var(--radius-pill)] border bg-surface px-4 py-2 font-sans text-base text-primary outline-none ${
                        tone ? TONE.field[tone] : "border-interactive"
                    }`}
                />
            ) : null}
        </div>
    );
}
